import type { OperationType, OperationTypeConfig } from '../types/activityType';

/**
 * Cache for operation types configuration
 */
let operationTypesCache: OperationTypeConfig[] | null = null;

/**
 * Set operation types cache
 */
export const setOperationTypesCache = (operationTypes: OperationTypeConfig[]): void => {
  operationTypesCache = operationTypes;
};

/**
 * Get operation type label
 */
export const getOperationTypeLabel = (operationType: OperationType): string => {
  if (!operationTypesCache) {
    throw new Error('Operation types cache not initialized. Call setOperationTypesCache first.');
  }
  
  const config = operationTypesCache.find(ot => ot.name === operationType);
  if (!config) {
    throw new Error(`Operation type "${operationType}" not found in cache.`);
  }
  
  return config.label;
};

/**
 * Get operation type color for UI
 */
export const getOperationTypeColor = (operationType: OperationType): 'success' | 'error' | 'info' | 'warning' | 'default' => {
  const colors: Record<OperationType, 'success' | 'error' | 'info' | 'warning'> = {
    STOCK_IN: 'success',
    STOCK_OUT: 'error',
    BETWEEN_STOCKS: 'info',
  };
  
  return colors[operationType] || 'default';
};

/**
 * Get operation type description
 */
export const getOperationTypeDescription = (operationType: OperationType): string => {
  if (!operationTypesCache) {
    throw new Error('Operation types cache not initialized. Call setOperationTypesCache first.');
  }
  
  const config = operationTypesCache.find(ot => ot.name === operationType);
  if (!config) {
    throw new Error(`Operation type "${operationType}" not found in cache.`);
  }
  
  return config.description;
};

/**
 * Check if a field is required for a specific operation type
 */
export const isFieldRequiredByOperationType = (
  operationType: OperationType,
  fieldName: string
): boolean => {
  if (!operationTypesCache) {
    throw new Error('Operation types cache not initialized. Call setOperationTypesCache first.');
  }
  
  const config = operationTypesCache.find(ot => ot.name === operationType);
  if (!config) {
    throw new Error(`Operation type "${operationType}" not found in cache.`);
  }
  
  const field = config.requiredFields.find(f => f.name === fieldName);
  return field?.isRequiredByDefault ?? false;
};
